import {useCallback, useEffect, useRef} from 'react';

/**
 * Contributor side of the `subEditActive` pattern (see {@link useSubEditActive}).
 *
 * A component that shows AT MOST ONE inline editor at a time (DocumentBlock, an
 * EditableTable host, …) calls `begin()` when it opens its editor and `end()`
 * when it closes. This contributes exactly one unit to the parent's edit
 * counter, with two guarantees that keep the parent count honest:
 *
 *   - Idempotent. Extra `begin()`s while already open, or extra `end()`s while
 *     already closed, are no-ops. So a re-open of the same editor (e.g.
 *     DocumentBlock's processCached restoring an unsaved draft on every mount)
 *     or a double-fired close path can't drift the count.
 *
 *   - Unwound on unmount. If the component unmounts while its editor is still
 *     open (ContentBlock scrolls it out of view / a card tab switch / a
 *     collapse / an ErrorBoundary swallowing a render error), the editor never
 *     fires its button-driven close, so without this the +1 would leak into the
 *     parent's counter and strand the card. The cleanup fires `onEnd` for the
 *     still-open editor so the net contribution across the whole lifecycle is 0.
 *
 * onBegin/onEnd are read through refs so a parent passing fresh callbacks each
 * render (or the unmount cleanup) always sees the latest, without re-creating
 * begin/end or re-running the unmount effect.
 */
export interface EditContribution {
    /** Signal the parent that this component's single editor opened. */
    begin: () => void;
    /** Signal the parent that this component's single editor closed. */
    end: () => void;
    /** True while this component is contributing an open editor to the parent. */
    isContributing: () => boolean;
}

export default function useEditContribution(
    onBegin: (() => void) | undefined,
    onEnd: (() => void) | undefined,
): EditContribution {
    const contributingRef = useRef(false);
    const onBeginRef = useRef(onBegin);
    const onEndRef = useRef(onEnd);

    // Keep the latest callbacks without re-creating begin/end or the unmount effect.
    useEffect(() => {
        onBeginRef.current = onBegin;
        onEndRef.current = onEnd;
    });

    const begin = useCallback(() => {
        if (contributingRef.current) return;
        contributingRef.current = true;
        onBeginRef.current?.();
    }, []);

    const end = useCallback(() => {
        if (!contributingRef.current) return;
        contributingRef.current = false;
        onEndRef.current?.();
    }, []);

    const isContributing = useCallback(() => contributingRef.current, []);

    // Unwind a still-open editor's contribution on unmount.
    useEffect(() => () => {
        if (contributingRef.current) {
            contributingRef.current = false;
            onEndRef.current?.();
        }
    }, []);

    return {begin, end, isContributing};
}
